import { useState } from "react";
import {
  useCreate,
  useGetList,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { Client, ContentPiece, PieceFormat, Sale } from "../types";
import { PIECE_FORMATS } from "./pieceModel";

const NO_ASSIGNEE = "none";

export const PieceFormDialog = ({
  clients,
  piece,
  defaultClientId,
  onClose,
  onSaved,
}: {
  clients: Client[];
  /** Editing when provided, creating otherwise. */
  piece?: ContentPiece;
  defaultClientId?: string;
  onClose: () => void;
  onSaved: (pieceId: Identifier) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [create, { isPending: isCreating }] = useCreate<ContentPiece>();
  const [update, { isPending: isUpdating }] = useUpdate<ContentPiece>();
  const { data: team = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false },
  });

  const [companyId, setCompanyId] = useState(
    piece ? String(piece.company_id) : (defaultClientId ?? ""),
  );
  const [title, setTitle] = useState(piece?.title ?? "");
  const [format, setFormat] = useState<PieceFormat>(piece?.format ?? "post");
  const [publishDate, setPublishDate] = useState(piece?.publish_date ?? "");
  const [dueDate, setDueDate] = useState(piece?.due_date ?? "");
  const [assigneeId, setAssigneeId] = useState(
    piece?.assignee_id != null ? String(piece.assignee_id) : NO_ASSIGNEE,
  );
  const [script, setScript] = useState(piece?.script ?? "");
  const [caption, setCaption] = useState(piece?.caption ?? "");
  const [error, setError] = useState<string | null>(null);

  const field = (name: string) =>
    translate(`resources.content_pieces.fields.${name}`);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!companyId || !title.trim()) {
      setError(translate("ra.validation.required"));
      return;
    }
    const values = {
      company_id: Number(companyId),
      title: title.trim(),
      format,
      publish_date: publishDate || null,
      due_date: dueDate || null,
      assignee_id: assigneeId === NO_ASSIGNEE ? null : Number(assigneeId),
      script: script.trim() || null,
      caption: caption.trim() || null,
    };
    const options = {
      onSuccess: (saved: ContentPiece) => {
        notify("resources.content_pieces.saved", { type: "success" });
        onSaved(saved.id);
      },
      onError: () => notify("ra.notification.http_error", { type: "error" }),
    };
    if (piece) {
      update(
        "content_pieces",
        { id: piece.id, data: values, previousData: piece },
        { ...options, mutationMode: "pessimistic" },
      );
    } else {
      create("content_pieces", { data: values }, options);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : null)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-[22px] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {translate(
              piece
                ? "resources.content_pieces.action.edit"
                : "resources.content_pieces.action.new",
            )}
          </DialogTitle>
        </DialogHeader>
        <form
          id="piece-form"
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>{field("company_id")}</Label>
            <Select
              value={companyId}
              onValueChange={(value) => {
                setCompanyId(value);
                setError(null);
              }}
              disabled={piece != null}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={translate(
                    "resources.content_pieces.select_client",
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={String(client.id)}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="piece-title">{field("title")}</Label>
            <Input
              id="piece-title"
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
                setError(null);
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{field("format")}</Label>
            <Select
              value={format}
              onValueChange={(value) => setFormat(value as PieceFormat)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PIECE_FORMATS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {translate(`resources.content_pieces.formats.${option}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{field("assignee_id")}</Label>
            <Select value={assigneeId} onValueChange={setAssigneeId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ASSIGNEE}>—</SelectItem>
                {team.map((member) => (
                  <SelectItem key={member.id} value={String(member.id)}>
                    {member.first_name} {member.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="piece-publish">{field("publish_date")}</Label>
            <Input
              id="piece-publish"
              type="date"
              value={publishDate}
              onChange={(event) => setPublishDate(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="piece-due">{field("due_date")}</Label>
            <Input
              id="piece-due"
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="piece-script">{field("script")}</Label>
            <Textarea
              id="piece-script"
              rows={5}
              value={script}
              onChange={(event) => setScript(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="piece-caption">{field("caption")}</Label>
            <Textarea
              id="piece-caption"
              rows={3}
              value={caption}
              onChange={(event) => setCaption(event.target.value)}
            />
          </div>
          {error ? (
            <p className="text-sm text-destructive sm:col-span-2">{error}</p>
          ) : null}
        </form>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="submit"
            form="piece-form"
            className="rounded-full"
            disabled={isCreating || isUpdating}
          >
            {translate("ra.action.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
